import React from "react";
import { C, themeColors } from "../../../lib/colors.js";
import { forecastDisplayName } from "../../../lib/forecast/displayName.js";
import {
  BackIcon,
  CalendarIcon,
  LayersIcon,
  BarsIcon,
  MailIcon,
  ChevronIcon,
  CollabIcon,
} from "../../../components/Icons.jsx";
import {
  TRACKING_CAD,
  TRACKING_CAD_SHORT,
  TRACKING_DEFAULTS,
  TRACKING_THR,
} from "../../../lib/tracking.js";
import { TrackingSlider, AlertThresholdSlider } from "../../../components/Sliders.jsx";
import { resolvedOutcome } from "../../../lib/forecast/resolvedOutcome.js";
import { rawOutcomeSpaceOf } from "../../../lib/forecast/outcomeSpace.js";
import {
  isReadOnlyRow,
  canChangeUpdates,
  ownerAttributionLabel,
} from "../../../lib/forecast/sharedRowGuards.js";
import {
  isSayerManaged,
  sayerManagedUpdates,
  SAYER_MANAGED_NOTE,
} from "../../../lib/featured/observerCopy.js";
import { ui } from "../../../lib/telemetry/instrument.js";
import { beginForecastSettingChange } from "../../../lib/telemetry/uiIntents.js";
import { Button } from "../../../components/ui/Primitives.jsx";
import SegmentedControl from "../../../components/SegmentedControl.jsx";
import { IconTrash } from "../../../components/ui/Icons.jsx";
import { keyHash } from "../../../components/keyHash.js";
import { sentenceCase } from "../../../components/sentenceCase.js";

/**
 * Whether the Leader alert is armed for `prefs`: an absent `leadingBin` means the shared default
 * in `shared/trackingDefaults.json`, the same file the server reads.
 */
const leadingBinArmed = (prefs) => prefs.leadingBin ?? TRACKING_DEFAULTS.leadingBin;

/**
 * The contract key at the foot of Tracking Settings, with a copy control.
 *
 * Replaces the debug key chip that used to ride every card's Resolves line. On the card it
 * was a developer affordance sitting in front of every user on every row, and its click had
 * to both copy AND fall through to select — two meanings on one target. Here it is one row,
 * in the panel already opened for that one forecast, where copying is the only thing it does.
 *
 * Shows the hash (User# prefix stripped, same as the old chip); copies the FULL key.
 */
/**
 * What the BACKEND committed this forecast to — binary or multi-value — for the debug panel.
 *
 * Read off the stored `forecast_value.outcome_space`, never off how the UI is rendering it:
 * under All Multiway a binary forecast is DRAWN as its two-slot distribution, so a
 * render-derived label would report "multiway" for a question the server answered as
 * binary — exactly the confusion this row exists to settle.
 *
 * A BooleanSpace is binary written as a PMF (see extractMultiway), so it reads BINARY. The
 * space's own kind is surfaced for a multi-value one, since "categorical" and "numeric"
 * resolve and label differently. Absent field → unknown rather than a guess: an older row
 * predates the field and saying "binary" would be an assertion nobody made.
 *
 * Keyed off the SPACE, not off the mass, and the bucket count rides along only when the
 * mass is actually there. A run can carry a space with no distribution behind it — a
 * degraded rerun whose space `withOutcomeSpace` preserved — and that row's shape is known
 * even though its answer is not. Requiring the mass hid exactly the case worth naming.
 */
function backendOutcomeKind(f) {
  const space = rawOutcomeSpaceOf(f) ?? rawOutcomeSpaceOf(f?.contract);
  if (!space) return null;
  if (space.kind === "boolean") return { label: "BINARY", detail: "boolean" };
  const mass = (f?.forecast_value ?? f?.contract?.forecast_value)?.representation?.mass;
  const buckets = Array.isArray(mass) && mass.length > 0 ? ` · ${mass.length} buckets` : "";
  return { label: "MULTIWAY", detail: `${space.kind || "kind unknown"}${buckets}` };
}

function ForecastIdRow({ fullKey }) {
  const hash = keyHash(fullKey);
  if (!hash) return null;
  const copy = () => {
    // Best-effort: a missing or insecure-context clipboard API is a silent no-op. Read
    // through globalThis so an ABSENT `navigator` (SSR, a DOM-less test) is undefined rather
    // than a ReferenceError — optional chaining does not protect an undeclared global. The
    // try/catch stays as the backstop for a clipboard that exists but rejects synchronously.
    // A copy the clipboard refused is a lost user action, warned (the row shows no "copied"
    // state that could show the miss instead).
    try {
      globalThis.navigator?.clipboard
        ?.writeText?.(fullKey)
        ?.catch((err) => console.warn("tracking_settings.copy_failed", err));
    } catch {
      /* clipboard unavailable — no-op */
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontFamily: '"IBM Plex Mono",monospace',
        fontSize: 11,
        letterSpacing: 0,
        color: C.gray,
        minWidth: 0,
      }}
    >
      <span style={{ flexShrink: 0 }}>FORECAST ID</span>
      <span
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: C.grayL,
        }}
      >
        {hash}
      </span>
      <button
        type="button"
        onClick={copy}
        title="Copy forecast ID"
        {...ui("forecast-settings.copy-id")}
        aria-label="Copy forecast ID"
        style={{
          background: "none",
          border: "none",
          padding: 2,
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: C.gray,
          flexShrink: 0,
        }}
      >
        {/* Two offset rounded rects — the conventional copy glyph. Inline rather than an
            icons.jsx export: nothing else needs it yet. */}
        <svg
          width="11"
          height="11"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.6" />
          <path d="M10.5 3.5 A1.6 1.6 0 0 0 9 2.5 H3.6 A1.6 1.6 0 0 0 2.5 4 V9.4 A1.6 1.6 0 0 0 3.5 10.5" />
        </svg>
      </button>
    </div>
  );
}

// Tracking Settings flyover. Slides over the Forecast Log on the same
// side (z above the log drawer per spec). Body holds the cadence
// slider, threshold slider, and notification toggles; footer holds the
// DELETE button. "Back to log" header closes via onClose. The handoff
// explicitly states this panel has NO probability display and NO
// "Reforecast now" button.
// `notifyEmailEnabled` (default false, fail-closed) is the `chatboard-notify-email`
// GOFF flag surfaced via /api/me: the EMAIL opt-in row shows only when the flag
// explicitly resolved true. Hidden => no email opt-in is offered and no email
// notify PATCH is ever sent (the row is the sole control that patches `email`).
// Hoisted out of TrackingSettingsPanel: a component declared during render gets
// a fresh identity each render and resets its own state (react-hooks/static-components).
// It's props-only (plus the module-level `C` palette), so `theme` comes in as a prop.
const Switch = ({ on, onClick, theme, "data-ui": uiId }) => (
  <div
    onClick={onClick}
    {...ui(uiId)}
    style={{
      width: 34,
      height: 18,
      borderRadius: 10,
      background: on ? (theme === "dev" || theme === "dusk" ? C.action : C.purpleDim) : C.border,
      position: "relative",
      flexShrink: 0,
      cursor: "pointer",
      transition: "background 0.2s",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 2,
        left: on ? 18 : 2,
        width: 14,
        height: 14,
        borderRadius: "50%",
        background: "#fff",
        transition: "left 0.2s",
      }}
    />
  </div>
);

// Square checkbox for the alert-trigger toggles. Deliberately NOT the `Switch`
// above: a Switch reads as "this feature is on/off", while these are additive
// conditions on one alert stream ("also tell me when…"), which is what a
// checkbox conventionally says. Hoisted for the same static-component reason.
const Checkbox = ({ on, onClick, label, hint, theme, "data-ui": uiId }) => (
  <div
    role="checkbox"
    {...ui(uiId)}
    aria-checked={on}
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 9,
      cursor: "pointer",
      padding: "8px 0",
    }}
  >
    <div
      style={{
        width: 14,
        height: 14,
        marginTop: 1,
        borderRadius: 3,
        flexShrink: 0,
        border: `1px solid ${on ? "transparent" : C.border}`,
        background: on
          ? theme === "dev" || theme === "dusk"
            ? C.action
            : C.purpleDim
          : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      {on && (
        <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
          <path
            d="M1.5 5.2 L3.9 7.5 L8.5 2.4"
            fill="none"
            stroke={C.bg}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
    <div>
      <div style={{ fontSize: 12, color: C.white }}>{label}</div>
      {hint && (
        <div style={{ fontSize: 11, color: C.gray, marginTop: 2, maxWidth: 240, lineHeight: 1.5 }}>
          {hint}
        </div>
      )}
    </div>
  </div>
);

// The in-app + threshold controls are always shown — only EMAIL is flag-gated.
// ── THE "SHARED" SECTION TAG ──────────────────────────────────────────────────
//
// The design requires this, and requires it BEFORE the fact: "The expanded
// forecast card must label which is which. A user changing the schedule or
// forecaster is changing it for everyone, and must be able to see that before
// they do."
//
// Only rendered when a forecast actually HAS other holders — a forecast nobody
// else can see has no "for everyone" to warn about, and labelling every private
// forecast's behaviour "SHARED" would train users to ignore the word by the time
// it matters. `isShared` is the owner-leg flag (true when at least one share row
// exists); `isOwn === false` is the recipient's side, where being told what is
// communal matters most.
//
// There is no "PERSONAL" counterpart any more. It used to tag MY ALERTS, but that
// heading now says "MY ALERTS" in the first person, which carries the same fact
// without a badge — and two badges side by side made the one that warns you about
// changing things for other people no louder than the one that didn't.
//
// ── WHY EVERYTHING IN THIS SECTION IS CALLED, NOT WRITTEN AS JSX ──────────────
//
// `SectionScope({...})`, `SettingsSection({...})` and `SettingsRow({...})` are
// INVOKED rather than written `<Like This />`. They are plain stateless functions
// of their props with no hooks, so a call and an element produce identical output
// — but only the call makes their text reachable by this file's test technique
// ("call the component, walk the un-invoked element tree"). A row's label lives in
// its `label` prop, and a prop on an element nobody invoked is invisible to that
// walker. Writing these as JSX silently empties the assertions that check what the
// panel says.
// The highlight colour for the panel's clickable settings — values, chips, the selected
// method, slider fills. Dawn uses its lavender here rather than its blue `purpleL`.
function settingsHighlight(theme, C) {
  return theme === "dusk" ? C.accent : C.purpleL;
}

function SectionScope({ theme }) {
  const C = themeColors(theme);
  const hl = settingsHighlight(theme, C);
  return (
    <span
      style={{
        marginLeft: 8,
        fontSize: 12,
        fontWeight: 500,
        padding: "1px 7px",
        borderRadius: 999,
        verticalAlign: 1,
        color: hl,
        border: `1px solid ${hl}`,
      }}
      title="Changing these changes them for everyone who has this forecast."
    >
      Shared
    </span>
  );
}

// ── ONE SETTINGS ROW ──────────────────────────────────────────────────────────
//
// The panel is a list of these: icon, label, sub-label, and on the right either a
// current value that opens the control, or a switch that IS the control.
//
// WHY THE CONTROL IS HIDDEN UNTIL ASKED FOR. Every control used to be on screen
// at once — two sliders and a radio group stacked vertically — so the page read as
// a wall of widgets with no summary of what the forecast was actually set to.
// Collapsed rows put the SETTINGS first ("On Triggers", "Deep", "±10%") and the
// means of changing them second, which is the order a reader wants: check, then
// adjust. It also lets the page fit without scrolling, which matters because this
// panel renders at ~330px wide in the log drawer.
//
// Expanding REPLACES the sub-label rather than pushing it down: the sub-label
// explains what the setting is for, which you no longer need once you are looking
// at its options, and keeping it would make an open row twice the height of a
// closed one.
function SettingsRow({
  icon,
  label,
  sub,
  value,
  // The right-hand node for a row that IS its own control (the email switch)
  // rather than a disclosure. Mutually exclusive with `value` in practice.
  control,
  // A node under the sub-label that is ALWAYS visible, for a row whose point is
  // one action rather than a setting with options. Distinct from `children`,
  // which appear only when the row is expanded: an action a reader has to go
  // looking for behind a chevron may as well not be offered.
  action,
  expanded = false,
  onToggle,
  children,
  theme,
  last = false,
  "data-ui": uiId,
}) {
  const C = themeColors(theme);
  const hl = settingsHighlight(theme, C);
  const openable = typeof onToggle === "function";
  return (
    <div
      style={{
        // The purple rail marks WHICH row you opened. On a page where expanding
        // swaps the sub-label for a control, the rail is what keeps the open row
        // reading as a row rather than as a new section.
        background: expanded ? C.rowInactive : "transparent",
        borderBottom: last ? "none" : `1px solid ${C.border}`,
        transition: "background 0.18s",
      }}
    >
      <div
        role={openable ? "button" : undefined}
        aria-expanded={openable ? expanded : undefined}
        {...ui(uiId)}
        tabIndex={openable ? 0 : undefined}
        onClick={openable ? onToggle : undefined}
        onKeyDown={
          openable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 11,
          padding: "13px 12px",
          cursor: openable ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <div style={{ color: hl, flexShrink: 0, marginTop: 1 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: C.white, lineHeight: 1.4 }}>{label}</div>
          {/* Open: the control lives here, where the sub-label was. */}
          {expanded ? (
            // The control lives INSIDE the element that toggles the row, so every
            // click and keypress meant for a slider, chip or radiogroup also
            // bubbled up and collapsed the row mid-interaction. Stopping both here
            // fixes it once for every control, rather than asking each one to
            // remember (ThresholdControl guarded its own clicks and not its keys;
            // EngineChoice and the sliders guarded neither).
            <div
              style={{ marginTop: 9 }}
              onClick={(e) => e.stopPropagation()}
              {...ui("forecast-settings.open-control", uiId)}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {children}
            </div>
          ) : sub ? (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 2, lineHeight: 1.45 }}>{sub}</div>
          ) : null}
          {action ? <div style={{ marginTop: 10 }}>{action}</div> : null}
        </div>
        {control ? (
          <div style={{ flexShrink: 0, marginTop: 1 }}>{control}</div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
              marginTop: 1,
              color: hl,
            }}
          >
            {/* The value is what the row is FOR when closed. While open it is
                dropped, except on the threshold row, whose control re-states it
                because the number is the thing being dragged. */}
            {!expanded && value ? (
              <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
            ) : null}
            {openable ? <ChevronIcon size={13} dir={expanded ? "down" : "right"} /> : null}
          </div>
        )}
      </div>
    </div>
  );
}

// A section heading plus its rows. `tag` is the SHARED badge node (or null),
// passed in rather than derived here so this stays presentational.
function SettingsSection({ title, tag, children, theme }) {
  const C = themeColors(theme);
  return (
    // 26 + 10%. The gap between sections is the only thing separating them now
    // that the heading rule is the sole divider.
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0,
          color: C.white,
          padding: "0 12px 8px",
        }}
      >
        {title}
        {tag}
      </div>
      {/* Rows are built by CALLING SettingsRow(...), so the `key` in each props
          object is an ordinary prop, not an element key, and the rows arrive as an
          unkeyed array. React 19's Children.toArray clones such elements marked as
          unkeyed and the reconciler still warns ("Each child in a list should have
          a unique key"), so each row is keyed here by its slot in a Fragment —
          sections are static lists, a null slot (a disabled row) stays a null. */}
      <div style={{ borderTop: `1px solid ${C.border}` }}>
        {(Array.isArray(children) ? children : [children]).map((row, i) =>
          row == null ? null : <React.Fragment key={i}>{row}</React.Fragment>,
        )}
      </div>
    </div>
  );
}

// Quick | Deep. Unchanged behaviour from the stacked layout it replaces — same
// ARIA radiogroup, same roving tabindex, same arrow-key nav — only the geometry
// moved (it sits inside a row now rather than under a heading).
function EngineChoice({ value, onPick }) {
  return (
    <SegmentedControl
      label="Depth for automatic updates"
      value={value}
      onChange={onPick}
      options={[
        { value: "quick", label: "Quick", ui: ui("forecast-settings.engine-option", "quick") },
        { value: "deep", label: "Deep", ui: ui("forecast-settings.engine-option", "deep") },
      ]}
    />
  );
}

// The threshold row's open state: the optional Leader chip, the 0-100 slider, and
// the live value restated underneath (you are dragging that number, so it must not
// vanish when the row's own right-hand value is dropped).
//
// The chip and the slider are two ALTERNATIVE triggers for one question — "what
// makes this worth an alert?". The threshold answers "how far it moved"; the
// Leader chip answers "which outcome is in front", which can change on a move far
// too small to cross any threshold.
//
// One drag is one `forecast_settings.change`: `startChange` opens it on the drag's first value
// change, and every `onChange(value, intent)` of that drag carries it, so the debounced writes
// it causes (even one sent mid-drag) send its id. The drag's end (or the control unmounting)
// hands it to `onRelease`, after which its last write ends it, and lets the next drag open anew.
function ThresholdControl({
  fill,
  pct,
  value,
  onChange,
  startChange,
  onRelease,
  leadOffered,
  leadOn,
  onToggleLead,
  theme,
}) {
  const C = themeColors(theme);
  const hl = settingsHighlight(theme, C);
  const dragIntentRef = React.useRef(null);
  const change = (v) => {
    if (!dragIntentRef.current) dragIntentRef.current = startChange?.() ?? null;
    onChange(v, dragIntentRef.current);
  };
  const commit = () => {
    const intent = dragIntentRef.current;
    dragIntentRef.current = null;
    if (intent) onRelease?.(intent);
  };
  // Collapsing the row mid-drag unmounts this before the pointer is released: the drag is over
  // then too, and the writes it already asked for decide its outcome.
  const commitRef = React.useRef(commit);
  React.useEffect(() => {
    commitRef.current = commit;
  });
  React.useEffect(() => () => commitRef.current(), []);
  return (
    <div onClick={(e) => e.stopPropagation()} {...ui("forecast-settings.threshold-control")}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {leadOffered ? (
          <div
            role="checkbox"
            aria-checked={leadOn}
            aria-label="Alert when a different outcome takes the lead"
            {...ui("forecast-settings.leader-alert")}
            tabIndex={0}
            onClick={onToggleLead}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleLead();
              }
            }}
            title="Also alert me when a different outcome takes the lead, even if the move is smaller."
            style={{
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 500,
              padding: "5px 10px",
              borderRadius: 999,
              cursor: "pointer",
              userSelect: "none",
              border: `1px solid ${leadOn ? hl : C.border}`,
              background: leadOn ? `${hl}22` : "transparent",
              color: leadOn ? hl : C.gray,
            }}
          >
            Leader changes
          </div>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <AlertThresholdSlider
            fill={fill}
            accent={theme === "dusk" ? hl : undefined}
            value={value}
            onChange={change}
            onCommit={commit}
          />
        </div>
      </div>
      {/* `leadOffered` says the Leader chip is DRAWN; `leadOn` says it is armed.
          Reading the former alone made this line claim "Leader + …" on every
          multi-value forecast, including ones whose chip the user had just
          switched off. */}
      <div className="sy-num" style={{ fontSize: 12, color: hl, fontWeight: 500, marginTop: 8 }}>
        {leadOffered && leadOn ? `Leader changes, or ±${pct} pts` : `±${pct} pts`}
      </div>
    </div>
  );
}

export function TrackingSettingsPanel({
  forecast,
  prefs,
  onPatch,
  applySchedule,
  // Whether Hourly is offered as a selectable auto-reforecast cadence. Off by
  // default (debug-menu toggle re-enables it); an already-hourly contract still
  // shows Hourly regardless so its state displays and can be changed.
  hourlyEnabled = false,
  onClose,
  onDelete,
  // "Run this question as mine" — offered only where the controls are not the
  // viewer's to touch. Optional: a caller with none wired renders no button
  // rather than a dead one.
  onRunAsMine,
  // The recipient's counterpart to onDelete — gives up only their own access.
  // Optional, like the rest of this panel's handlers.
  onRevokeMyAccess,
  // Opens the share dialog. A HANDLER, not the button node it replaced: the
  // control is now a row in the ACCESS section, so the panel owns its
  // presentation while App keeps the gate (`canOfferSharing`) and the
  // `ui.share.dialog_open` telemetry that has to fire on the click itself.
  // Absent => no ACCESS row, the same no-dead-controls rule the rest of this
  // panel follows.
  onManageAccess,
  // WHICH ROW IS OPEN, and the request to change it — owned by the
  // `TrackingSettings` wrapper below, not by this function.
  //
  // This component stays HOOK-FREE on purpose. Its tests (and forecastLog.jsx's
  // neighbours generally) call it as a plain function and walk the un-invoked
  // element tree; a `useState` in here throws outside a renderer and would force
  // every one of those tests onto a DOM harness. Keeping the state one level up
  // costs two props and preserves the technique.
  openSection = null,
  onToggleSection,
  theme,
  notifyEmailEnabled = false,
  // `chatboard-leading-bin-alerts` via /api/me. Fail-closed default false: with
  // the notifier's lead trigger off, the chip must not appear — checked, since
  // the pref defaults on — promising an email nothing will send.
  leadingBinAlertsEnabled = false,
  // Gates FORECAST BEHAVIOR + MY ALERTS: both configure tracking, which an
  // unwatchlisted row does not do. ACCESS is unconditional. Defaults true so a
  // caller predating watchlists keeps the full panel.
  watchlisted = true,
  // Debug Force-Resolution status for previewing the resolved (greyed) panel.
  resolutionOverride = null,
  // "Now" (effectiveToday) for the unresolved-past-date check (threaded from App effectiveToday).
  nowMs,
  // Debug UI on → show the contract key at the foot of the panel. It used to sit on every
  // card's Resolves line, which put a developer affordance in front of every user on every
  // row; here it is one line, behind the debug flag, next to the forecast it identifies.
  debugUI = false,
}) {
  if (!forecast) return null;
  const open = openSection;
  // `undefined`, not a no-op, when there is no handler: SettingsRow reads
  // `typeof onToggle === "function"` to decide whether the row LOOKS openable, so
  // returning a function unconditionally drew a chevron and a role=button on rows
  // that did nothing when clicked (TrackingSettingsPanel used directly, as the
  // tests and the dev gallery do).
  const toggle = (key) => (onToggleSection ? () => onToggleSection(key) : undefined);
  // Grey + disable the controls once there's a resolution VERDICT (matching the
  // card + timeline) — not on contractStatus alone, so a policy-resolved contract
  // whose verdict hasn't landed stays interactive rather than falsely "resolved".
  const outcome = resolvedOutcome(forecast, nowMs, resolutionOverride);
  const resolved = !!outcome;
  // The "Leader" chip: a multi-value forecast alerts when a different bucket
  // leads, a binary one (or a two-bin one) when it crosses 50% — Yes ↔ No
  // (notify/leadingBin.js::binaryLead). NOT on a CONTINUOUS space: it has no
  // buckets and no scalar, so the notifier skips it and the chip could never fire.
  const outcomeKind = (rawOutcomeSpaceOf(forecast) ?? rawOutcomeSpaceOf(forecast.contract))?.kind;
  const leadOffered = leadingBinAlertsEnabled && outcomeKind !== "continuous";
  // ── WHAT A COLLABORATOR SEES IN THIS PANEL ────────────────────────────────
  // The cadence and forecaster (the QUESTION's, so an in-org holder may set them),
  // and their OWN alerts — the wire carries the viewer's `notify`, never the
  // owner's, and `onPatch` routes their write accordingly. Not DELETE, which acts
  // on the forecast rather than on your access to it.
  const shared = isReadOnlyRow(forecast);
  // Updates and alerts are settings of the forecast, not of the watchlist: your own forecast
  // always shows them; a shared one shows them once it is on your watchlist.
  const tracking = watchlisted || !shared;
  // The cadence, though, is a property of the QUESTION, so an in-org
  // collaborator may set it. Server-reported — see the guard's own doc.
  // A copy of one of Sayer's forecasts can be changed too: the change makes it
  // the user's own first (App's applySchedule, via useSayerCopyOwnership).
  const sayerManaged = isSayerManaged(forecast);
  const cadenceWritable = canChangeUpdates(forecast) || (sayerManaged && !shared);
  const managedUpdates = sayerManagedUpdates(forecast);
  // Are there OTHER HOLDERS to warn about? Either this row is shared out (the
  // owner's `isShared`) or it was shared TO the viewer (`isOwn === false`). A
  // forecast with neither is nobody else's business and gets no tag — see
  // SectionScope's own note on why labelling everything would defeat the point.
  const hasOtherHolders = forecast.isShared === true || shared;
  const HOURLY_CAD_IDX = 2;
  const visibleCadIdxs = TRACKING_CAD.map((_v, i) => i).filter(
    (i) => i !== HOURLY_CAD_IDX || hourlyEnabled || prefs.cadIdx === HOURLY_CAD_IDX,
  );
  const hl = settingsHighlight(theme, C);
  const sliderFill = theme === "dusk" ? hl : theme === "dev" ? C.action : C.purpleDim;
  const thrPct = TRACKING_THR[prefs.thrIdx];
  // OFFERED and ARMED, which are different things: the chip is drawn whenever
  // the flag is on, but `leadingBin: false` disarms it (absent = the shared default).
  const leadActive = leadOffered && leadingBinArmed(prefs);
  return (
    <>
      {/* Header */}
      {/* No bottom rule: it read as a line sitting on top of the FORECAST
          BEHAVIOR heading rather than as the header's own edge. The section
          headings carry their own rule, which is enough separation. */}
      <div style={{ padding: "12px 12px 4px" }}>
        <Button
          variant="ghost"
          size="sm"
          icon={<BackIcon size={14} />}
          onClick={onClose}
          {...ui("forecast-settings.back")}
          style={{ marginLeft: -4 }}
        >
          Back to forecasts
        </Button>
        <div style={{ padding: "12px 2px 0" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.white }}>
            {tracking ? "Tracking settings" : "Access settings"}
          </div>
          <div
            style={{
              fontSize: 13,
              color: C.gray,
              lineHeight: 1.45,
              marginTop: 4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {forecastDisplayName(forecast)}
          </div>
        </div>
      </div>
      {/* Body */}
      <div
        style={{
          padding: "16px 2px",
          flex: 1,
          overflowY: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* `inert` (not just pointerEvents:none) so the greyed controls are also
          removed from keyboard focus / tab order / AT when resolved — a
          keyboard user can't change a resolved forecast's settings. */}
        <div
          inert={resolved}
          style={resolved ? { opacity: 0.35, pointerEvents: "none" } : undefined}
        >
          {/* ── FORECAST BEHAVIOR ─────────────────────────────────────────────
              The two settings that belong to the QUESTION rather than to one
              person, which is why this is the section carrying the SHARED tag.
              Every holder lives on one timeline, so an in-org collaborator sets
              them for everyone; an out-of-org one sees them and cannot move them
              (`inert`, so they also leave the tab order), with the reason stated
              below rather than left as a control that quietly ignores them. */}
          {tracking && (
            <div
              inert={!cadenceWritable}
              style={!cadenceWritable ? { opacity: 0.45, pointerEvents: "none" } : undefined}
            >
              {SettingsSection({
                title: "Updates",
                tag: hasOtherHolders ? SectionScope({ theme }) : null,
                theme,
                children: [
                  SettingsRow({
                    key: "cadence",
                    ...ui("forecast-settings.cadence"),
                    theme,
                    icon: <CalendarIcon size={17} />,
                    label: "Update automatically",
                    sub: "How often Sayer re-forecasts this question",
                    value: managedUpdates?.cadence ?? sentenceCase(TRACKING_CAD[prefs.cadIdx]),
                    expanded: open === "cadence",
                    onToggle: toggle("cadence"),
                    children: (
                      <TrackingSlider
                        {...ui("forecast-settings.cadence-option")}
                        uiKeys={visibleCadIdxs}
                        fill={sliderFill}
                        accent={hl}
                        opts={visibleCadIdxs.map((i) => sentenceCase(TRACKING_CAD[i]))}
                        idx={Math.max(0, visibleCadIdxs.indexOf(prefs.cadIdx))}
                        fmt={(_v, pos) => sentenceCase(TRACKING_CAD_SHORT[visibleCadIdxs[pos]])}
                        onPick={(pos, { via } = {}) => {
                          // A label names its control; a drag of the track (no data-ui of
                          // its own) does not, so it carries no origin rather than a wrong one.
                          const intent = beginForecastSettingChange("cadence", forecast.id, {
                            origin: via === "drag" ? null : "forecast-settings.cadence-option",
                          });
                          applySchedule(forecast.id, visibleCadIdxs[pos], undefined, { intent });
                        }}
                      />
                    ),
                  }),
                  // Writes the tier tag into schedule.params.engine via applySchedule
                  // (cadence preserved); the Observer runs that tier on each scheduled
                  // reforecast once auto-reforecast is on. Shown even when cadence is
                  // None so the control doesn't disappear.
                  SettingsRow({
                    key: "engine",
                    ...ui("forecast-settings.engine"),
                    theme,
                    icon: <LayersIcon size={17} />,
                    label: "Depth",
                    sub: "Quick is faster; Deep is more thorough",
                    value: managedUpdates?.depth ?? (prefs.engine === "quick" ? "Quick" : "Deep"),
                    expanded: open === "engine",
                    onToggle: toggle("engine"),
                    last: true,
                    children: (
                      <EngineChoice
                        value={prefs.engine}
                        onPick={(k) => {
                          const intent = beginForecastSettingChange("engine", forecast.id, {
                            origin: "forecast-settings.engine-option",
                          });
                          applySchedule(forecast.id, prefs.cadIdx, k, { intent });
                        }}
                      />
                    ),
                  }),
                ],
              })}
            </div>
          )}
          {tracking && sayerManaged && (
            <div
              style={{
                margin: "-14px 12px 26px",
                maxWidth: 260,
                fontSize: 12,
                color: C.gray,
                lineHeight: 1.5,
              }}
            >
              {`${SAYER_MANAGED_NOTE} Changing these makes it your own forecast.`}
            </div>
          )}
          {tracking && !cadenceWritable && !sayerManaged && (
            <div style={{ margin: "-14px 12px 26px", maxWidth: 260 }}>
              <div style={{ fontSize: 12, color: C.gray, lineHeight: 1.5 }}>
                This forecast belongs to another organization. You can follow its schedule, but
                changing it needs a forecast of your own.
              </div>
              {/* Rendered only when a handler is wired, so the panel cannot show an
                action that goes nowhere — the same rule the rest of this panel
                follows. It runs a FRESH forecast of this question owned by the
                viewer (an ordinary submit; see App's `runQuestionAsMine`), which
                is what the design asks for rather than a copy of the row. */}
              {onRunAsMine && (
                <Button
                  size="sm"
                  onClick={() => onRunAsMine(forecast)}
                  {...ui("forecast-settings.run-as-mine")}
                  style={{ marginTop: 8, width: "100%" }}
                >
                  Forecast this question myself
                </Button>
              )}
            </div>
          )}
          {/* ── MY ALERTS ─────────────────────────────────────────────────────
              Shown for a recipient too — their own row, their own inbox. The
              heading's first person is what says these are not communal; there is
              no PERSONAL badge any more (see SectionScope). */}
          {tracking &&
            SettingsSection({
              title: "My alerts",
              tag: null,
              theme,
              children: [
                SettingsRow({
                  key: "threshold",
                  ...ui("forecast-settings.threshold"),
                  theme,
                  icon: <BarsIcon size={17} />,
                  label: "Alert me when it moves by",
                  sub: leadActive ? "Leader changes also alert you" : "Alerts show on the card",
                  value: `±${thrPct} pts`,
                  expanded: open === "threshold",
                  onToggle: toggle("threshold"),
                  last: !notifyEmailEnabled,
                  children: (
                    <ThresholdControl
                      theme={theme}
                      fill={sliderFill}
                      pct={thrPct}
                      value={prefs.thrIdx}
                      startChange={() =>
                        beginForecastSettingChange("threshold", forecast.id, {
                          origin: "forecast-settings.threshold-control",
                        })
                      }
                      onChange={(v, intent) =>
                        onPatch(forecast.id, { thrIdx: v }, { intent, holding: true })
                      }
                      onRelease={(intent) => onPatch(forecast.id, null, { intent })}
                      leadOffered={leadOffered}
                      leadOn={leadingBinArmed(prefs)}
                      onToggleLead={() => {
                        const intent = beginForecastSettingChange("leader_alert", forecast.id, {
                          origin: "forecast-settings.leader-alert",
                        });
                        onPatch(
                          forecast.id,
                          { leadingBin: prefs.leadingBin === false },
                          { intent },
                        );
                      }}
                    />
                  ),
                }),
                // In-app alerts are always on (the row flag + rail badge on a big
                // move); email is the only opt-in, and the only row here that is its
                // own control rather than a disclosure.
                notifyEmailEnabled
                  ? SettingsRow({
                      key: "email",
                      theme,
                      icon: <MailIcon size={17} />,
                      label: "Email alerts",
                      sub: "Sent to your account email when an alert fires",
                      last: true,
                      control: (
                        <Switch
                          {...ui("forecast-settings.email-toggle")}
                          on={prefs.email}
                          onClick={() => {
                            const intent = beginForecastSettingChange("email", forecast.id, {
                              origin: "forecast-settings.email-toggle",
                            });
                            onPatch(
                              forecast.id,
                              { email: !prefs.email, thrIdx: prefs.thrIdx },
                              { intent },
                            );
                          }}
                          theme={theme}
                        />
                      ),
                    })
                  : null,
              ],
            })}
        </div>
        {/* ── ACCESS ────────────────────────────────────────────────────────
            OUTSIDE the `inert={resolved}` wrapper, like delete below: a settled
            forecast's tracking is frozen, but who can see it is not — a resolved
            forecast can still be shared, and a recipient can still leave it.

            TWO DIFFERENT ROWS, deliberately not one with two labels. An owner
            MANAGES who can see this; a recipient can only see who told them and
            give up their own access. One row with swapped text would invite a
            recipient to read "Manage access" as something they hold. */}
        {(shared ? !!onRevokeMyAccess : !!onManageAccess) &&
          SettingsSection({
            title: "Access",
            tag: null,
            theme,
            children: shared
              ? SettingsRow({
                  theme,
                  icon: <CollabIcon size={17} direction="in" />,
                  label: ownerAttributionLabel(forecast),
                  sub: "You can stop following this forecast at any time",
                  last: true,
                  // ALWAYS visible, not behind a chevron: this row's whole point
                  // is the one action, and there is no setting here to disclose.
                  // Not red and not a trash icon either — nothing is destroyed
                  // for anyone else, and dressing it as a destructive action
                  // would misrepresent what it does.
                  action: (
                    <Button
                      size="sm"
                      onClick={() => onRevokeMyAccess(forecast)}
                      {...ui("forecast-settings.revoke-access")}
                    >
                      Remove my access
                    </Button>
                  ),
                })
              : SettingsRow({
                  theme,
                  // ~15% over the 17 the other row icons use — this one reads
                  // small for its weight as the section's only owner action.
                  icon: <CollabIcon size={19.5} direction="out" />,
                  label: "Manage access",
                  sub: "Who can see and edit this forecast",
                  // The STATE, not a count. A number would need the owner-only
                  // shares read fetched on every panel open, to say something
                  // the word already says.
                  value: forecast.isShared ? "Shared" : "Private",
                  onToggle: onManageAccess,
                  ...ui("forecast-settings.manage-access"),
                  last: true,
                }),
          })}
        {resolved && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                marginTop: 26,
                color: C.white,
                fontSize: 14,
                fontWeight: "bold",
                letterSpacing: 0,
                // hex-alpha (`panel` + alpha) can't be composed from a var() string,
                // so read the raw-hex palette. A translucent grey wash over the near-
                // opaque panel greys the chip out slightly; lavender (purpleL) outline.
                background: `linear-gradient(${themeColors(theme).gray}33, ${themeColors(theme).gray}33), ${themeColors(theme).panel}e6`,
                padding: "9px 16px",
                borderRadius: 8,
                border: `1px solid ${hl}`,
                boxShadow: `0 4px 16px ${C.shadow}`,
              }}
            >
              {outcome && !outcome.namesOutcome ? "Forecast closed" : "Forecast resolved"}
            </div>
          </div>
        )}
        {/* The panel FOOT: the debug diagnostics and the delete button, pinned
            to the bottom as one group. `marginTop: auto` lives here rather than
            on the button, so the id/kind lines sit directly above delete instead
            of being left behind against the last settings section with the
            button floated away below them. Collapses to normal flow when the
            settings are tall enough to scroll. */}
        <div style={{ marginTop: "auto" }}>
          {debugUI && (forecast.contractKey || forecast._obs?.contractKey) ? (
            <div style={{ padding: "0 12px" }}>
              <ForecastIdRow fullKey={forecast.contractKey || forecast._obs?.contractKey} />
            </div>
          ) : null}
          {/* Directly under the id, and debug-only for the same reason: it answers "what did
            the backend actually do with this question", which is a diagnostic, not a user
            fact. Rendered even when the id is absent — the kind is the more useful half. */}
          {debugUI
            ? (() => {
                const k = backendOutcomeKind(forecast);
                if (!k) return null;
                return (
                  <div
                    style={{
                      fontFamily: '"IBM Plex Mono",monospace',
                      fontSize: 11,
                      letterSpacing: 0,
                      color: C.gray,
                      display: "flex",
                      gap: 6,
                      padding: "0 12px",
                    }}
                  >
                    <span>{k.label}</span>
                    <span style={{ opacity: 0.7 }}>{k.detail}</span>
                  </div>
                );
              })()
            : null}
          {/* Owner only. A recipient's counterpart is the ACCESS row above —
              non-owners cannot delete. Last thing in the panel, under the debug
              rows, and deliberately OUTSIDE the settings wrapper's `inert={resolved}`:
              a resolved (or closed-unresolved) forecast's settings are frozen, but it
              must stay deletable. This panel is the only delete surface the manager
              has — its rows carry none — so graying it out left a settled forecast
              impossible to remove (prod bug 560fe3f2). The click still goes through
              the delete confirm popup. */}
          {!shared && (
            <div
              data-delete-forecast=""
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "4px 0 8px",
              }}
            >
              <Button
                variant="danger"
                size="sm"
                icon={<IconTrash />}
                onClick={() => onDelete(forecast)}
                {...ui("forecast-settings.delete")}
              >
                Delete forecast
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
